// Copyright (c) Docugami, Inc. All rights reserved.

import { ActionButton, IconButton } from "@fluentui/react";
import { editableTokenColor, editTokenColor, emptyGray, hoveredTokenColor, primaryTheme, smallShadow, ZIndices } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { useAppSelector } from "hooks/redux";
import { useSdvEntityQuery } from "hooks/SdvEntityQuery";
import { useSdvRenderable } from "hooks/SdvRenderable";
import { TokenRange } from "networking/api";
import { useEffect, useMemo, useState } from "react";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId } from "sdv/engine/Component";
import { Engine } from "sdv/engine/Engine";
import { folderIdSelector, selectTokens } from "store/dgml/dgmlSelectors";
import { selectSelectedChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";
import { TokensTextParser } from "utils/TokensTextParser";

const InputContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    z-index: ${ZIndices.ExternalCanvasComponent};

    position: absolute;

    border: 2px solid ${editTokenColor};
    border-radius: 3px;

    box-shadow: ${smallShadow};

    max-width: 400px;
    max-height: 400px;

    height: fit-content;
    width: fit-content;

    background-color: ${primaryTheme.palette.white};

    ${props => !!props.hidden && 'display: none'};
`;

const Input = styled.input`
    border: none;
    margin: 5px;
    width: 8em;
    height: 20px;
    font-size: 12px;

    :focus {
        outline: none;
    }
`;

const SelectorContainer = styled.div`
    display: flex;
    flex-direction: row;
    place-items: center;
    margin: 5px;
    height: 20px;
    width: 100%;
    overflow: auto;
`;

const SelectableToken = styled.button`
    display: flex;
    background-color: ${editableTokenColor};
    border: none;
    margin: 3px;
    cursor: pointer;

    :hover {
        background-color: ${hoveredTokenColor};
    }
`;

const Divider = styled.div`
    border-right: 1px solid ${emptyGray};
    height: 12px;
`;

interface TextEditorInputProps {
    engine: Engine | null;
    documentId: string;
    onCancel: () => void;
    onAccept: (tokenRange: TokenRange, text: string) => void;
}

const maxLength = 100;

const editorComponents: ComponentId[] = [ComponentId.ExternalRender, ComponentId.TextEditor, ComponentId.Position];
const selectionComponents: ComponentId[] = [ComponentId.EditTextSelectionComponent, ComponentId.TokenRange];

export const TextEditorInput = (props: TextEditorInputProps): JSX.Element => {
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(0);
    const [value, setValue] = useState('');
    const [deleteConfirmationVisible, setDeletionConfirmationVisible] = useState(false);

    const folderId = folderIdSelector(props.documentId);

    const { ref: inputRef, entity } = useSdvRenderable(editorComponents, props.engine);
    const { entities: editTextSelectionEntities } = useSdvEntityQuery(selectionComponents, props.engine);
    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);

    const allTokens = useAppSelector(state => !!folderId ? selectTokens(state, folderId.clusterId, folderId.fileId) : undefined);

    const [tokens, tokenRangeText] = useMemo(() => {
        if (!!entity && !!allTokens && !!editTextSelectionEntities.length) {
            const currentRange = editTextSelectionEntities[0].getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            var tokensText = new TokensTextParser(allTokens);
            const tokens = tokensText.getTokensForRange(currentRange);
            return [tokens, tokens.map(tokenIdx => allTokens[tokenIdx].text)];
        }

        return [undefined, undefined]
    }, [entity, allTokens, editTextSelectionEntities])

    useEffect(() => {
        if (!!tokenRangeText && tokenRangeText.length !== 1) {
            setSelectedIndex(undefined);
        }

        return () => setSelectedIndex(0)
    }, [tokenRangeText])

    useEffect(() => {
        if (!!tokenRangeText?.length && selectedIndex !== undefined && selectedIndex < tokenRangeText.length) {
            setValue(tokenRangeText[selectedIndex]);
        }
    }, [tokenRangeText, selectedIndex])

    function isValid() {
        const text = value.trim();
        return !!tokenRangeText && selectedIndex !== undefined && selectedIndex < tokenRangeText.length && text !== tokenRangeText[selectedIndex];
    }

    function onAccept() {
        if (!value.trim()) {
            // The user is deleting the token, so allow them to confirm the choice
            setDeletionConfirmationVisible(true);
        }
        else {
            accept();
        }
    }

    const accept = () => {
        if (!!tokens && selectedIndex !== undefined && selectedIndex < tokens.length) {
            props.onAccept?.({ fromTok: tokens[selectedIndex], toTok: tokens[selectedIndex] + 1 }, value);
        }
    }

    function getContent() {
        if (!tokenRangeText) {
            return;
        }

        if (selectedIndex !== undefined) {
            return (
                <>
                    <Input
                        value={value}
                        autoFocus
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter" && isValid()) {
                                onAccept();
                                e.stopPropagation();
                                e.preventDefault();
                            }
                            else if (e.key === "Escape") {
                                props.onCancel?.();
                                e.stopPropagation();
                                e.preventDefault();
                            }
                        }}
                        maxLength={maxLength} />
                    {tokenRangeText.length > 1 && (
                        <IconButton
                            title="Go back"
                            onClick={() => setSelectedIndex(undefined)}
                            iconProps={{
                                iconName: 'Undo',
                                styles: { root: { fontSize: '13px' } }
                            }} />
                    )}
                    <ConfirmationDialog
                        hidden={!deleteConfirmationVisible}
                        title={"Delete text"}
                        subText={"Are you sure you want to delete this text from your document? This action cannot be undone."}
                        confirmButtonText="DELETE"
                        dismissButtonText="Cancel"
                        dismissedCallback={() => { setDeletionConfirmationVisible(false); setValue(tokenRangeText?.[selectedIndex] ?? '') }}
                        confirmedCallback={accept}
                        dialogContentProps={{ showCloseButton: false }} />
                </>
            )
        } else {
            return (
                <SelectorContainer>
                    {tokenRangeText.map((text, index) => <SelectableToken key={tokens![index]} onClick={() => setSelectedIndex(index)}>{text}</SelectableToken>)}
                </SelectorContainer>
            )
        }
    }

    return (
        <InputContainer ref={inputRef} hidden={!entity || !entity.hasComponent(ComponentId.Renderable) || !selectedChunkInstance}>
            {getContent()}
            <Divider />
            <ActionButton title="OK" disabled={!isValid()} onClick={onAccept}>OK</ActionButton>
        </InputContainer>
    )
}
